import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X, Loader2, DollarSign } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CostLinkModal = ({ projectId, job, open, onClose, onLinked }) => {
  const [codes, setCodes] = useState([]);
  const [codeId, setCodeId] = useState('');
  const [qty, setQty] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCodeId(''); setSaving(false);
    const count = job?.result?.summary?.totalCount ?? job?.result?.count ?? job?.result?.area ?? '';
    setQty(count !== '' && count != null ? String(count) : '');
    axios.get(`${API}/projects/${projectId}/cost-codes`).then((r) => setCodes(r.data || [])).catch(() => setCodes([]));
  }, [open, job, projectId]);

  if (!open) return null;
  const selected = codes.find((c) => c.id === codeId);
  const total = selected && qty ? (selected.unitCost * parseFloat(qty || 0)).toFixed(2) : null;

  const save = async () => {
    if (!codeId || qty === '') { toast.error('Pick a cost code and quantity'); return; }
    setSaving(true);
    try {
      const existing = (job?.costLinks || []).map((l) => ({ costCodeId: l.costCodeId, quantity: l.quantity }));
      const links = [...existing.filter((l) => l.costCodeId !== codeId), { costCodeId: codeId, quantity: parseFloat(qty) }];
      const r = await axios.patch(`${API}/analysis-jobs/${job.id}/cost-links`, { links });
      toast.success('Linked to cost code');
      onLinked && onLinked(r.data);
      onClose();
    } catch (e) { toast.error('Failed to link'); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="cost-link-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2"><DollarSign className="w-5 h-5 text-emerald-500" /> Link Cost Code</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="cost-link-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          {codes.length === 0 ? (
            <p className="text-sm text-slate-500">No cost codes for this project yet. Add them from the Cost Codes page first.</p>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Cost Code</label>
                <select value={codeId} onChange={(e) => setCodeId(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg" data-testid="cost-link-select">
                  <option value="">Select…</option>
                  {codes.map((c) => <option key={c.id} value={c.id}>{c.code} — {c.description} (₹{c.unitCost}/{c.unit})</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Quantity</label>
                <input type="number" step="0.01" value={qty} onChange={(e) => setQty(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg" data-testid="cost-link-qty" />
              </div>
              {total != null && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-800" data-testid="cost-link-total">
                  {qty} × ₹{selected.unitCost} = <span className="font-bold">₹{total}</span>
                </div>
              )}
            </>
          )}
        </div>
        <div className="flex justify-end gap-3 px-5 py-4 border-t border-slate-200">
          <button onClick={onClose} className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200">Cancel</button>
          <button onClick={save} disabled={saving || codes.length === 0} className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-60 flex items-center gap-2" data-testid="cost-link-save">{saving && <Loader2 className="w-4 h-4 animate-spin" />} Link</button>
        </div>
      </div>
    </div>
  );
};

export default CostLinkModal;
